/**
 * Cliente HTTP común: adjunta el JWT y reacciona a las sesiones caducadas.
 *
 * Por qué existe: hasta la Fase 1 del RBAC casi ningún endpoint exigía token, así
 * que las llamadas se hacían con `axios` global o `fetch()` nativo sin cabeceras.
 * Al proteger la API, cada una de esas llamadas devolvería 401. RTK Query ya
 * adjuntaba el token por su cuenta (`redux/services/api.js`); esto cubre todo lo
 * demás con un único punto de instalación.
 *
 * Ver `.claude/plans/roles-permisos-rbac.md` → "Hotfix frontend que viaja con Fase 1".
 */
import axios from 'axios';
import { clearViewAs, viewAsHeaders } from './view-as';

/**
 * Endpoints públicos por diseño. Importa distinguirlos por dos motivos:
 *  - no tiene sentido mandarles el token
 *  - un 401 suyo es una respuesta legítima (credenciales incorrectas en el login),
 *    NO una sesión caducada: cerrar sesión ahí rompería la pantalla de login.
 */
const PUBLIC_PATTERNS = [
  /\/auth\/login$/,
  /\/auth\/mobilelogin$/,
  /\/auth\/update$/,
  /\/auth\/delete$/,
  /\/auth\/invite\//,
  /\/club-invite\//,
  /\/director\/auth\//,
  /\/public\//,
  /\/api\/server$/,
];

export const isPublicUrl = (url) =>
  typeof url === 'string' && PUBLIC_PATTERNS.some((re) => re.test(url));

export const getToken = () => {
  try { return localStorage.getItem('token'); } catch { return null; }
};

const clearSession = () => {
  try {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('activeCamp');
    // Mismos borrados que `logout()` del contexto: esta función es la que se ejecuta
    // cuando la sesión caduca sola, y dejaba atrás dos cosas que sí sobrevivían al
    // siguiente login en el mismo navegador — los permisos del usuario anterior y,
    // peor, su vista previa "Ver como", cuyas cabeceras dejaban la cuenta entrante
    // respondiendo 403 en cada petición sin ningún control visible para salir.
    localStorage.removeItem('permissions');
  } catch { /* almacenamiento no disponible */ }
  clearViewAs();
};

const LOGIN_PATH = '/auth/login';

/**
 * Sesión caducada o revocada. Se limpia el almacenamiento y se manda al login.
 * Se usa `location.replace` a propósito: así el botón "atrás" no devuelve a una
 * pantalla que ya no puede cargar datos.
 */
export const handleExpiredSession = () => {
  clearSession();
  if (!window.location.pathname.startsWith(LOGIN_PATH)) {
    window.location.replace(LOGIN_PATH);
  }
};

/** Decide si un 401 significa "tu sesión ya no vale" o "tus credenciales son malas". */
const isSessionExpiry = (status, url) => status === 401 && Boolean(getToken()) && !isPublicUrl(url);

let installed = false;

/** Instala los interceptores globales de axios. Idempotente. */
export function installHttpInterceptors() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token && !isPublicUrl(config.url)) {
      config.headers = config.headers || {};
      // No pisar una cabecera puesta a mano (p.ej. el portal de directores,
      // que usa `director_token` y no este).
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Vista previa "Ver como": el servidor degrada la identidad si procede.
      Object.assign(config.headers, viewAsHeaders());
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (isSessionExpiry(error?.response?.status, error?.config?.url)) {
        handleExpiredSession();
      }
      return Promise.reject(error);
    }
  );
}

/**
 * `fetch` con el mismo comportamiento que axios: adjunta el token y trata el 401
 * como sesión caducada. Sustituto directo de `fetch()` en las páginas que aún no
 * usan RTK Query.
 */
export async function authFetch(input, init = {}) {
  const url = typeof input === 'string' ? input : input?.url;
  const token = getToken();

  const headers = new Headers(init.headers || {});
  if (token && !isPublicUrl(url) && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
    Object.entries(viewAsHeaders()).forEach(([k, v]) => headers.set(k, v));
  }

  const response = await fetch(input, { ...init, headers });

  if (isSessionExpiry(response.status, url)) {
    handleExpiredSession();
  }
  return response;
}
